/**
 * Tour registry. One GuidedOnboardingLayer resolves the active tour from the
 * current route: the clinic Insurance-Verification tour on /treatment-plans and
 * the FTC-Personnel tour on /ftc-tasks. The backend `tour` app is already
 * multi-tour (TourProgress.tour_name, unique per user+tour).
 *
 * A config carries everything route-specific the engine needs:
 *   tourName       persistence key (TourProgress.tour_name)
 *   route          hash route this tour lives on
 *   steps          the manifest array
 *   boardId        a board-only [data-onboarding-id] used to detect "we're here"
 *                  (for the TopBar replay nav)
 *   modalId        [data-onboarding-id] marking "the current view is the modal"
 *                  (for insideModal step targeting / idle-step filtering)
 *   ensureDemo     async (officeId) => {} to seed demo data, or null
 *   reminderLabel  human label for the "finish the tour?" reminder
 *   hostName       side-panel host name
 */
import { STEPS as INSURANCE_STEPS, TOUR_ROUTE as INSURANCE_ROUTE } from "./tourSteps";
import { STEPS as FTC_STEPS, TOUR_ROUTE as FTC_ROUTE } from "./tourSteps.ftc";
import { ensureTourDemo, ensureFtcTourDemo } from "./onboardingController";

export const DEFAULT_TOUR_ROUTE = INSURANCE_ROUTE;

export const TOUR_CONFIGS = [
  {
    // NOTE tourName is the TourProgress.tour_name persistence key (unique per
    // user+tour) and is NOT a label — it stays "insurance_verification" even though
    // the page is now called Treatment Planning. Renaming it would orphan every user's saved
    // progress and the CRM's tour-status column.
    tourName: "insurance_verification",
    route: INSURANCE_ROUTE, // "/treatment-plans"
    steps: INSURANCE_STEPS,
    boardId: "tab-all-patients",
    modalId: "insurance-modal-tabs",
    ensureDemo: ensureTourDemo,
    reminderLabel: "Treatment Planning",
    hostName: "Smilepass guide",
  },
  {
    tourName: "ftc_tasks",
    route: FTC_ROUTE, // "/ftc-tasks"
    steps: FTC_STEPS,
    boardId: "ftc-task-table",
    modalId: "insurance-modal-tabs", // the FTC "Insurance" button opens the same modal
    ensureDemo: ensureFtcTourDemo,
    reminderLabel: "FTC Tasks",
    hostName: "FTC training guide",
  },
];

// The config whose route matches the current location (clean or hash form), else
// null. Route is the only gate we need: only FTC users can reach /ftc-tasks and
// only clinic users reach /treatment-plans (sidebar is role-scoped).
export const resolveTourConfig = (location, hashPath) => {
  const path = (location && location.pathname) || "";
  return TOUR_CONFIGS.find((c) => path === c.route || hashPath === c.route) || null;
};
